import { Component, ComponentInterface, Element, Event, EventEmitter, h, Host, Listen, Prop, State } from '@stencil/core';
import {
  renderFormError,
  renderTooltipOnBottom,
  renderTooltipOnLeft
} from '../../../shared/form-controls/jui-form-control.utils';
import { JuiControlSize, JuiValidationEvents } from '../../../shared/form-controls/jui-form-control.model';
import { IconSize, IconVariant } from '../../jui-icon/jui-icon.model';
import { ButtonDisplay, ButtonSize, ButtonVariant } from '../../jui-button/jui-button.model';
import { RenderedFileEntry } from './file-input.model';
import { niceBytes } from '../../../shared/form-controls/utils';
import { TranslationUtils } from '../../../shared/TranslationUtils';
import { BadgePosition, BadgeSize } from '../../jui-badge/jui-badge.model';

@Component({
  tag: 'jui-file-input',
  styleUrl: 'jui-file-input.scss',
  shadow: true
})
export class JuiFileInput implements ComponentInterface {
  @Element() el: HTMLElement;
  /**
   * The file input label.
   */
  @Prop() label: string;

  /**
   * The file input button title;
   */
  @Prop()
  actionlabel: string;

  /**
   * The form name for the file input control.
   */
  @Prop() name = '';

  /**
   * Marking the file input control as required. Defaults to false
   */
  @Prop() required = false;

  /**
   * Marking the file input field as disabled. Defaults to false
   */
  @Prop() disable: boolean;

  /**
   * Marking the file input field as readonly. Defaults to false
   */
  @Prop() readonly: boolean;

  /**
   * Allow multiple uploads, defaults to true
   */
  @Prop() multiple = true;

  /**
   * Specific prop for mobile rendering
   */
  @Prop() mobile = false;

  /**
   * Prop for rendering a smaller jui-badge
   */
  @Prop() smallbadge = false;

  /**
   * The file input value.
   */
  @Prop({ attribute: 'files', reflect: true, mutable: true })
  files: any;

  /**
   * Set the size of the file input. Options are 'md', 'lg' or 'sm'. Defaults to 'md'
   */
  @Prop() size: JuiControlSize = JuiControlSize.md;

  /**
   * Accepted file types
   */
  @Prop() accept: string = null;

  /**
   * Max file size in bytes
   */
  @Prop() maxfilesize = 1000000; // 1MB

  /**
   * Max file uploads
   */
  @Prop() maxuploads = 10;

  /**
   * Optional tooltip string
   */
  @Prop() tooltip: string = null;

  /**
   * Optional help string
   */
  @Prop() help: string = null;

  /**
   * Optional data-qa string
   */
  @Prop() dataqastring: string = null;

  /**
   * The event to trigger validation check
   */
  @Prop() validation: JuiValidationEvents = JuiValidationEvents.onLoad;

  /**
   * The errors for the file input
   */
  @Prop() errors: boolean | { [key: string]: string };

  /**
   * The validation messages for the file input
   */
  @Prop() messages: string | { [key: string]: string };

  /**
   * Output event emitted when change is fired
   */
  @Event({ composed: true }) public juiChange: EventEmitter<File[] | null>;

  @Listen('keydown')
  keydownHandler(evt: KeyboardEvent): void {
    if (evt.key === 'Escape') {
      this.cancelUpload();
    }
  }

  @State()
  private focused = false;

  @State()
  private tooltipLeft = false;

  @State()
  private tooltipLeftBottom = false;

  private errorMessages = null;

  @State()
  private showFileUploader = null;

  @State()
  private isInvalid = false;

  @State()
  private maxUploadsExceeded = false;

  @State()
  private showUploaded = false;

  @State()
  private selectedFileList: File[] = [];

  @State()
  private filesOnLoad: any;

  @State()
  private fileEntries: RenderedFileEntry[] = [];

  @State() i18nUtils: TranslationUtils;

  @State() i18ns: any;

  @State()
  private filesAmount = 0;

  private maxFileSizeDisplay: string;
  private acceptedTypeDisplay: string;

  componentDidRender(): void {
    if (this.tooltip) {
      this.renderTooltips();
    }
  }

  componentWillUpdate(): void {
    if (!this.files) {
      this.selectedFileList = [];
    } else {
      this.filesAmount = this.files?.length ?? 0;
      this.initFiles();
    }
    this.renderError();
  }

  componentWillLoad(): void {
    this.initTranslations();
    if (this.validation === JuiValidationEvents.onLoad) {
      this.renderError();
    }
    if (!this.multiple) {
      this.maxuploads = 1;
    }

    this.filesAmount = this.files?.length ?? 0;
    this.initFiles();
    this.maxFileSizeDisplay = niceBytes(this.maxfilesize);
    if (this.accept) {
      this.acceptedTypeDisplay = `( ${this.accept.split(',').join(', ')} )`;
    } else {
      this.acceptedTypeDisplay = null;
    }
  }

  private initTranslations(): void {
    this.i18nUtils = TranslationUtils.instance;
    this.i18ns = {};
    this.i18ns.cancel = this.i18nUtils.translate('Buttons.cancel.text', 'Cancel');
    this.i18ns.browse = this.i18nUtils.translate('Buttons.browse.text', 'Browse');
    this.i18ns.invalidFileFormat = this.i18nUtils.translate('common.modals.invalidFile.title', 'Invalid file format');
    this.i18ns.fileSizeExceeded = this.i18nUtils.translate(
      'common.modals.maximumFileSize.title',
      'Maximum file size exceeded'
    );
    this.i18ns.dropFilesHere = this.i18nUtils.translate('components.juiFileInput.dropFilesHere', 'Drop files here');
    this.i18ns.fileUploaded = this.i18nUtils.translate('components.juiFileInput.fileUploaded', 'file uploaded');
    this.i18ns.filesUploaded = this.i18nUtils.translate('components.juiFileInput.filesUploaded', 'files uploaded');
    this.i18ns.addFiles = this.i18nUtils.translate('components.juiFileInput.addFiles', 'Add files');
    this.i18ns.clearAll = this.i18nUtils.translate('components.juiFileInput.clearAll', 'Clear all');
    this.i18ns.done = this.i18nUtils.translate('components.juiFileInput.done', 'Done');
    this.i18ns.uploadFiles = this.i18nUtils.translate('components.juiFileInput.uploadFiles', 'Upload files');
    this.i18ns.maximumFileSize = this.i18nUtils.translate(
      'components.juiFileInput.maximumFileSize',
      'Maximum file size'
    );
    this.i18ns.maximumUploadsExceeded = this.i18nUtils.translate(
      'components.juiFileInput.maximumUploadsExceeded',
      'Maximum uploads exceeded'
    );
    this.i18ns.max = this.i18nUtils.translate('common.max', 'maximum');
    this.i18ns.dismiss = this.i18nUtils.translate('components.juiFileInput.dismiss', 'Dismiss');
    this.i18ns.addMore = this.i18nUtils.translate('components.juiFileInput.addMore', 'Add More');
    this.i18ns.or = this.i18nUtils.translate('components.juiFileInput.or', 'or');
  }

  private initFiles() {
    this.selectedFileList = [];
    if (this.files && this.files.length) {
      if (this.files instanceof FileList) {
        for (let i = 0; i < this.files.length; i++) {
          this.selectedFileList.push(this.files.item(i));
        }
      } else {
        for (let i = 0; i < this.files.length; i++) {
          this.selectedFileList.push(this.files[i]);
        }
      }
    }
  }

  private getActionSize(): ButtonSize {
    switch (this.size) {
      case JuiControlSize.md:
        return ButtonSize.sm;
      case JuiControlSize.lg:
        return ButtonSize.lg;
      case JuiControlSize.sm:
        return ButtonSize.xs;
    }
  }

  beginUpload(): void {
    this.showFileUploader = true;
    this.filesOnLoad = this.files ? [...this.files] : [];
    this.updateMobileDisplay();
  }

  cancelUpload(): void {
    this.showFileUploader = false;
    this.files = this.filesOnLoad;
    this.fileEntries.forEach((fileEntry: RenderedFileEntry) => {
      window.URL.revokeObjectURL(fileEntry.img);
    });
    this.resetFileInput();
  }

  handleDragOver(evt): void {
    evt.preventDefault();
  }

  dropFiles(evt): void {
    evt.preventDefault();

    if (evt.dataTransfer.items) {
      [...evt.dataTransfer.items].forEach(item => {
        if (item.kind === 'file') {
          const file = item.getAsFile();
          this.selectedFileList.push(file);
        }
      });
    } else {
      [...evt.dataTransfer.files].forEach(file => {
        this.selectedFileList.push(file);
      });
    }

    this.files = [...this.selectedFileList];
  }

  browseDevice(): void {
    (this.el.shadowRoot.querySelector('#fileInput') as HTMLInputElement).click();
  }

  handleUpload(evt): void {
    const files = evt.target.files as FileList;
    for (let i = 0; i < files.length; i++) {
      this.selectedFileList.push(files.item(i));
    }
    this.files = [...this.selectedFileList];
    this.updateMobileDisplay();
  }

  removeFile(name: string): void {
    // revoke object url
    const deletedFile = this.fileEntries.find(item => item.fileName === name);
    window.URL.revokeObjectURL(deletedFile.img);
    this.selectedFileList = this.selectedFileList.filter(item => item.name !== name);

    this.files = this.createAFileList();
    this.resetFileInput();
    this.updateMobileDisplay();
  }

  clearFiles(): void {
    this.revokeFileCache();
    this.selectedFileList = [];
    this.files = this.createAFileList();
    this.resetFileInput();
  }

  resetFileInput(): void {
    const fileInputEl = this.el.shadowRoot.querySelector('input#fileInput') as HTMLInputElement;
    fileInputEl.value = null;
  }

  private updateMobileDisplay() {
    if (!this.mobile) {
      return;
    }
    const uploads = this.files && this.files.length;
    this.setUploaded(uploads);
  }

  private setUploaded(uploaded: boolean): void {
    this.showUploaded = uploaded;
  }

  uploadDone(): void {
    this.showFileUploader = false;
    this.files = this.createAFileList();
    this.filesAmount = this.files.length;
    this.juiChange.emit(this.files);
  }

  private createAFileList(): File[] {
    return this.selectedFileList;
  }

  private isImageFileType(type: string): boolean {
    return type.includes('image/');
  }

  private revokeFileCache(): void {
    this.fileEntries.forEach(entry => window.URL.revokeObjectURL(entry.img));
    this.fileEntries = [];
  }

  private isFileTypeInvalid(file: File): boolean {
    if (!this.accept) {
      return false;
    }
    const acceptedFileTypesArray = this.accept.split(',');
    const fileExtension = `.${file.type.split('/')[1]}`;

    // add special check for jpg/jpeg
    if (fileExtension === '.jpeg') {
      return !acceptedFileTypesArray.includes(fileExtension) && !acceptedFileTypesArray.includes('.jpg');
    }
    return !acceptedFileTypesArray.includes(fileExtension);
  }

  createFileEntries(): void {
    this.revokeFileCache();
    this.isInvalid = false;
    this.maxUploadsExceeded = false;
    // this.addMoreDisabled = false;
    for (const file of this.selectedFileList) {
      if (file.size > this.maxfilesize) {
        this.isInvalid = true;
        this.fileEntries.push({
          img: '',
          fileName: file.name,
          type: file.type,
          isImage: this.isImageFileType(file.type),
          fileSizeTooBig: true,
          invalidFileType: false
        });
      } else if (this.isFileTypeInvalid(file)) {
        this.isInvalid = true;
        this.fileEntries.push({
          img: '',
          fileName: file.name,
          type: file.type,
          isImage: this.isImageFileType(file.type),
          fileSizeTooBig: false,
          invalidFileType: true
        });
      } else {
        const url = window.URL.createObjectURL(file);
        this.fileEntries.push({
          img: url,
          fileName: file.name,
          type: file.type,
          isImage: this.isImageFileType(file.type),
          fileSizeTooBig: false,
          invalidFileType: false
        });
      }
    }

    if (this.selectedFileList.length > this.maxuploads) {
      this.isInvalid = true;
      this.maxUploadsExceeded = true;
    }
  }

  renderFileEntries() {
    this.createFileEntries();
    return this.fileEntries.map(x => {
      const invalidText = x.fileSizeTooBig ? this.i18ns.fileSizeExceeded : this.i18ns.invalidFileFormat;
      return [
        <div class="jui-file-input__file" data-testid="uploaded-file">
          <div class="jui-file-input__details">
            <div class="jui-file-input__thumb">
              {!x.fileSizeTooBig && !x.invalidFileType ? (
                x.isImage ? (
                  <img class="jui-file-input__thumb-img" src={x.img} alt="" />
                ) : (
                  <div>
                    <jui-icon
                      name="insert_drive_file"
                      color={IconVariant.low}
                      size={IconSize.lg}
                      isButton={false}
                    ></jui-icon>
                  </div>
                )
              ) : (
                <div class="jui-file-input__thumb-img jui-file-input__thumb-img--invalid">
                  <jui-icon
                    name="priority_high"
                    color={IconVariant.danger}
                    size={IconSize.lg}
                    isButton={false}
                  ></jui-icon>
                </div>
              )}
            </div>
            {!x.fileSizeTooBig && !x.invalidFileType ? (
              <div class="jui-file-input__label">{x.fileName}</div>
            ) : (
              <div class="jui-file-input__invalid">
                <div class="jui-file-input__label jui-file-input__label--invali">{x.fileName}</div>
                <div class="jui-file-input__label jui-file-input__label--invalid--sm">{invalidText}</div>
              </div>
            )}
          </div>
          <div class="jui-file-input__file-actions">
            <jui-icon
              data-testid="remove-file"
              name="delete"
              color={IconVariant.low}
              size={IconSize.sm}
              onClick={() => this.removeFile(x.fileName)}
            ></jui-icon>
          </div>
        </div>
      ];
    });
  }

  render() {
    // qa tags
    const dataQaTagLabel = this.dataqastring ? `${this.dataqastring}_formFieldLabel` : null;
    const dataQaTagInput = this.dataqastring ? `${this.dataqastring}_formField` : null;
    const dataQaTagUploadBtn = this.dataqastring ? `${this.dataqastring}_formFieldUploadBtn` : null;

    const errorFragment = this.errorMessages ? this.errorMessages : null;
    const badgeSize = this.smallbadge ? BadgeSize.sm : BadgeSize.md;
    const badgeFragment = (
      <jui-badge data-testid="uploaded-count" outlined={true} position={BadgePosition.topRight} size={badgeSize}>
        {this.filesAmount}
      </jui-badge>
    );

    const fileEntries = this.renderFileEntries();
    const fileCount = fileEntries.length;

    const fileUploaderFragment = this.showFileUploader ? (
      <div class="jui-file-input__modal">
        <div
          data-testid="file-modal"
          class={{
            'jui-file-input__modal-content': true,
            [`jui-file-input__modal-content--mobile`]: !!this.mobile
          }}
        >
          <div class="jui-file-input__modal-close">
            <jui-icon
              data-testid="close-action"
              name="close"
              color={IconVariant.current}
              size={IconSize.sm}
              onClick={this.cancelUpload.bind(this)}
            ></jui-icon>
          </div>

          <div class="jui-file-input__upload-area">
            {this.mobile && this.showUploaded ? (
              <div class="jui-file-input__uploaded">
                <div class="jui-file-input__header">
                  {fileCount}/{this.maxuploads} {this.i18ns.filesUploaded}
                </div>
                <div class="jui-file-input__files">{fileEntries}</div>
                {this.maxUploadsExceeded ? (
                  <div class="jui-file-input__error" data-testid="max-upload-error">
                    {this.i18ns.maximumUploadsExceeded} {this.selectedFileList.length} ({this.i18ns.max}:{' '}
                    {this.maxuploads})
                  </div>
                ) : null}
                <div class="jui-file-input__actions">
                  <jui-button
                    class="jui-file-input__action"
                    size={ButtonSize.lg}
                    display={ButtonDisplay.ghost}
                    color={ButtonVariant.secondary}
                    expand={true}
                    onClick={this.browseDevice.bind(this)}
                  >
                    {this.i18ns.addMore}
                  </jui-button>
                  <jui-button
                    data-testid="clear-action"
                    class="jui-file-input__action"
                    color={ButtonVariant.secondary}
                    display={ButtonDisplay.ghost}
                    size={ButtonSize.lg}
                    expand={true}
                    onClick={this.clearFiles.bind(this)}
                  >
                    {this.i18ns.clearAll}
                  </jui-button>
                </div>
                <jui-button
                  data-testid="done-action"
                  class="jui-file-input__action"
                  disabled={this.isInvalid}
                  size={ButtonSize.lg}
                  expand={true}
                  onClick={this.uploadDone.bind(this)}
                >
                  {this.i18ns.done}
                </jui-button>

                <jui-button
                  data-testid="dismiss-action"
                  class="jui-file-input__action"
                  size={ButtonSize.md}
                  color={ButtonVariant.subtle}
                  expand={true}
                  onClick={this.cancelUpload.bind(this)}
                >
                  {this.i18ns.dismiss}
                </jui-button>
              </div>
            ) : null}

            {this.mobile && !this.showUploaded ? (
              <div class="jui-file-input__upload">
                <div class="jui-file-input__drop-text">
                  <div class="jui-file-input__drop-text jui-file-input__drop-text--sub">
                    {this.i18ns.maximumFileSize}: {this.maxFileSizeDisplay}
                  </div>
                  {this.accept ? (
                    <div class="jui-file-input__drop-text jui-file-input__drop-text--sub">
                      {this.acceptedTypeDisplay}
                    </div>
                  ) : null}
                </div>
                <jui-button
                  class="jui-file-input__action"
                  size={ButtonSize.lg}
                  expand={true}
                  onClick={() => this.browseDevice()}
                >
                  {this.i18ns.browse}
                </jui-button>

                <jui-button
                  class="jui-file-input__action"
                  size={ButtonSize.md}
                  color={ButtonVariant.subtle}
                  expand={true}
                  onClick={this.cancelUpload.bind(this)}
                >
                  {this.i18ns.dismiss}
                </jui-button>
              </div>
            ) : null}

            {!this.mobile ? (
              <div class="jui-file-input__upload">
                <div
                  class="jui-file-input__drop"
                  onClick={() => this.browseDevice()}
                  onDrop={e => this.dropFiles(e)}
                  onDragOver={e => this.handleDragOver(e)}
                >
                  <div class="jui-file-input__drop-icon">
                    <div>
                      <jui-icon
                        name="file_upload"
                        isButton={false}
                        size={IconSize.xl}
                        color={IconVariant.current}
                      ></jui-icon>
                    </div>
                  </div>

                  <div class="jui-file-input__drop-text jui-file-input__drop-text--lg">{this.i18ns.dropFilesHere}</div>
                  <div class="jui-file-input__drop-text">{this.i18ns.or}</div>
                  <jui-button class="jui-file-input__action" size={this.getActionSize()}>
                    {this.i18ns.browse}
                  </jui-button>

                  <div class="jui-file-input__drop-text">
                    <div class="jui-file-input__drop-text jui-file-input__drop-text--sub">
                      {this.i18ns.maximumFileSize}: {this.maxFileSizeDisplay}
                    </div>
                    {this.accept ? (
                      <div class="jui-file-input__drop-text jui-file-input__drop-text--sub">
                        {this.acceptedTypeDisplay}
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
            ) : null}

            {!this.mobile ? (
              <div class="jui-file-input__uploaded">
                <div class="jui-file-input__header">
                  {fileCount}/{this.maxuploads} {this.i18ns.filesUploaded}
                </div>
                <div class="jui-file-input__files" data-testid="desktop-files">
                  {fileEntries}
                </div>
                {this.maxUploadsExceeded ? (
                  <div class="jui-file-input__error" data-testid="max-upload-error">
                    {this.i18ns.maximumUploadsExceeded} {this.selectedFileList.length} ({this.i18ns.max}:{' '}
                    {this.maxuploads})
                  </div>
                ) : null}
                <div class="jui-file-input__uploaded-actions">
                  <div>
                    <jui-button
                      data-testid="clear-action"
                      class="jui-file-input__uploaded-action"
                      color={ButtonVariant.low}
                      size={ButtonSize.sm}
                      onClick={this.clearFiles.bind(this)}
                    >
                      {this.i18ns.clearAll}
                    </jui-button>
                  </div>
                  <div>
                    <jui-button
                      data-testid="done-action"
                      disabled={this.isInvalid}
                      size={ButtonSize.sm}
                      onClick={this.uploadDone.bind(this)}
                    >
                      {this.i18ns.done}
                    </jui-button>
                  </div>
                </div>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    ) : null;

    const helpText = this.help ? <div class="jui-control__help">{this.help}</div> : null;
    const buttonTitle = this.actionlabel && this.actionlabel !== '' ? this.actionlabel : this.i18ns.uploadFiles;

    const tooltip = this.tooltip ? (
      <div
        class={{
          'jui-control__tooltip': true,
          [`jui-control__tooltip--input-${this.size}`]: true
        }}
      >
        <jui-icon
          class={{ 'jui-control__tooltip-icon': true }}
          name="help_outline"
          cursor="help"
          color={IconVariant.info}
          size={IconSize.xs}
          tabindex={-1}
        ></jui-icon>
        <span
          class={{
            'jui-control__tooltip-text': true,
            'jui-control__tooltip-text--left': this.tooltipLeft && !this.tooltipLeftBottom,
            'jui-control__tooltip-text--left-bottom': this.tooltipLeftBottom
          }}
        >
          {this.tooltip}
        </span>
      </div>
    ) : null;

    return (
      <Host data-qa={this.dataqastring || null}>
        <div
          data-testid="file-input"
          class={{
            'jui-control': true,
            [`jui-control--${this.size}`]: true
          }}
        >
          {this.label ? (
            <label
              {...(dataQaTagLabel && { 'data-qa': dataQaTagLabel })}
            class={{
              'jui-control__label': true,
              'jui-control__label--has-value': !!this.files,
              'jui-control__label--has-focus': !!this.focused,
              'jui-control__label--disabled': !!this.disable,
              'jui-control__label--narrower': !!this.tooltip
            }}
          >
            {this.required ? `${this.label} *` : this.label}
          </label>
        ) : null}
        {helpText}
        {tooltip}
        <div
          class={{
            'jui-control__btn-container': true,
            'jui-control__btn-container--margin': !!this.label
          }}
        >
          <div class="jui-control__btn">
            <jui-button
              data-testid="file-input-main-action"
              {...(dataQaTagUploadBtn && { 'data-qa': dataQaTagUploadBtn })}
              disabled={this.readonly || this.disable}
              onClick={() => this.beginUpload()}
              size={this.getActionSize()}
            >
              {buttonTitle}
            </jui-button>
            {badgeFragment}
          </div>
        </div>
        <input
          data-testid="hidden-file-input"
          {...(dataQaTagInput && { 'data-qa': dataQaTagInput })}
          onChange={e => this.handleUpload(e)}
          class="jui-control__input"
          type="file"
          id="fileInput"
          accept={this.accept}
          multiple={this.multiple}
          files={this.files}
          hidden
        />
        {errorFragment}
        {fileUploaderFragment}
        </div>
      </Host>
    );
  }

  private renderTooltips(): void {
    this.tooltipLeft = renderTooltipOnLeft(this.el, 'jui-control__btn-container');
    if (this.tooltipLeft) {
      this.tooltipLeftBottom = renderTooltipOnBottom(this.el, 'jui-control__btn-container');
    }
  }

  private renderError(): void {
    this.errorMessages = renderFormError(this.errors, this.messages);
  }
}
